import { formatCurrency } from '../utilities/_commerce'
import { pushGa4Event } from '../utilities/_analytics'

export function eventDetailsForm () {
  return {
    form: { eventName: '', eventVenue: '', standName: '', standNumber: '', startDate: '', endDate: '', eventNotes: '' },
		venueOptions: [],
    shippingAddress: {
      fullName: '',
      organization: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      stateValue: '',
      zipCode: '',
      locality: '',
      administrativeArea: '',
      postalCode: '',
      countryCode: ''
    },
    errors: {}, notice: '', loading: false, debugEnabled: true,

    init () {
      this.debug('Component mounted', {
        hasFormRef: !!this.$refs?.dateForm
      });

			this.handleEventVenueChange()
      this.$nextTick(() => this.handleVenueAddressChange())
      this.$watch('shippingAddress.city', (value) => {
        this.shippingAddress.locality = value || ''
      })
      this.$watch('shippingAddress.stateValue', (value) => {
        this.shippingAddress.administrativeArea = value || ''
      })
      this.$watch('shippingAddress.zipCode', (value) => {
        this.shippingAddress.postalCode = value || ''
      })

      this.$watch('form.eventVenue', (value) => {
        if (this.form.eventName === 'other') {
          this.shippingAddress.fullName = value || ''
        }
      })

			this.$watch('form.eventName', () => {
				window.dispatchEvent(new Event('formsUpdate'))
			})

      pushGa4Event('enquiry_step_view', {
        enquiry_step_name: 'dates and venue',
        enquiry_step_number: 2
      })
    },

    debug (message, payload = null) {
      if (!this.debugEnabled) return;
      if (payload) {
        console.log(`[eventDetailsForm] ${message}`, payload);
        return;
      }
      console.log(`[eventDetailsForm] ${message}`);
    },

    validate () {
      this.errors = {};

      const isRequiredFieldInvalid = (selector, value) => {
        const field = this.$refs.dateForm?.querySelector(selector);
        if (!field || !field.required) return false;
        const normalizedValue = (value || '').toString().trim();
        const invalid = !normalizedValue || normalizedValue.toLowerCase() === 'please choose';
        this.debug('Field validation', {
          selector,
          exists: !!field,
          required: !!field?.required,
          value: normalizedValue,
          invalid
        });
        return invalid;
      };

      // Simple client-side checks (mirror required fields currently shown)
      if (isRequiredFieldInvalid('#eventName', this.form.eventName)) {
        this.errors.eventName = 'Pick an event name';
      }

      const isOtherVenue = this.form.eventName === 'other';
      const eventVenueSelector = isOtherVenue ? '#order-eventVenueText' : '#order-eventVenue';

      if (isRequiredFieldInvalid(eventVenueSelector, this.form.eventVenue)) {
        this.errors.eventVenue = 'Pick an event venue';
      }
      if (isRequiredFieldInvalid('#standName', this.form.standName)) {
        this.errors.standName = 'Stand name is required';
      }
      if (isRequiredFieldInvalid('#standNumber', this.form.standNumber)) {
        this.errors.standNumber = 'Stand number is required';
      }
      if (isRequiredFieldInvalid('#startDate', this.form.startDate)) {
        this.errors.startDate = 'Start date is required';
      }
      if (isRequiredFieldInvalid('#endDate', this.form.endDate)) {
        this.errors.endDate = 'End date is required';
      }
      this.debug('Validation result', {
        isValid: Object.keys(this.errors).length === 0,
        errors: this.errors,
        form: this.form
      });
      return Object.keys(this.errors).length === 0;
    },

    submit () {
      this.debug('Submit clicked');
      this.notice = '';
      this.errors = {};
      const valid = this.validate();
      if (!valid) {
        this.debug('Submit blocked by validation', { errors: this.errors });
        return;
      }

      this.loading = true;
      pushGa4Event('enquiry_step_submit', {
        enquiry_step_name: 'dates and venue',
        enquiry_step_number: 2
      })
      this.debug('Validation passed; submitting native form', {
        action: this.$refs?.dateForm?.action || window.location.pathname
      });

      // Submit natively after Alpine validation passes.
      if (!this.$refs?.dateForm) {
        this.debug('Cannot submit: dateForm ref missing');
        this.errors._form = 'Debug: form ref missing (see console).';
        this.loading = false;
        return;
      }

      this.$refs.dateForm.submit();
    },

		getLineItemPrice(price, salePrice, qty) {
			if (salePrice) {
				return formatCurrency(salePrice * qty)
			} else {
				return formatCurrency(price * qty)
			}
		},

		getTotal(price) {
			return formatCurrency(this.$store.cart.totalPrice)
		},

		handleEventVenueChange($event) {
			const target = $event ? $event.target : this.$refs.dateForm.querySelector('#eventName');

			if (!target) return

			const selectedOption = target.querySelector('option:checked')

			if (!selectedOption) return

			const venuesData = selectedOption?.dataset?.venues
      const selectedStartDate = selectedOption?.dataset?.startDate || ''
      const selectedEndDate = selectedOption?.dataset?.endDate || ''
      const selectedVenueId = selectedOption?.dataset?.selectedVenue || ''

      if (selectedStartDate) {
        this.form.startDate = selectedStartDate
      }

      if (selectedEndDate) {
        this.form.endDate = selectedEndDate
      }

			if (venuesData) {
				this.venueOptions = JSON.parse(venuesData)
        if (this.venueOptions.length === 1) {
          this.form.eventVenue = this.venueOptions[0].id
          this.$nextTick(() => this.handleVenueAddressChange())
        } else if (!this.form.eventVenue && selectedVenueId && this.venueOptions.some((option) => `${option.id}` === `${selectedVenueId}`)) {
          this.form.eventVenue = selectedVenueId
          this.$nextTick(() => this.handleVenueAddressChange())
        } else if (!this.venueOptions.some((option) => `${option.id}` === `${this.form.eventVenue}`)) {
          this.form.eventVenue = ''
          this.clearShippingAddress()
        } else {
          this.$nextTick(() => this.handleVenueAddressChange())
        }
        return
			}

      this.venueOptions = []
      this.form.eventVenue = ''
      this.clearShippingAddress()
		},

    clearShippingAddress () {
      this.shippingAddress = {
        fullName: '',
        organization: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        stateValue: '',
        zipCode: '',
        locality: '',
        administrativeArea: '',
        postalCode: '',
        countryCode: ''
      }
    },

    handleVenueAddressChange ($event = null) {
      const venueSelect = $event?.target || this.$refs?.dateForm?.querySelector('#order-eventVenue')

      if (!venueSelect) {
        this.clearShippingAddress()
        return
      }

      const selectedOption = venueSelect.querySelector('option:checked')
      const addressData = selectedOption?.dataset?.address

      if (!addressData) {
        this.clearShippingAddress()
        return
      }

      try {
        const parsedAddress = JSON.parse(addressData)
        const city = parsedAddress.city || parsedAddress.locality || ''
        const stateValue = parsedAddress.stateValue || parsedAddress.administrativeArea || ''
        const zipCode = parsedAddress.zipCode || parsedAddress.postalCode || ''
        const countryCode = (parsedAddress.countryCode || '').toString().trim().toUpperCase()

        this.shippingAddress = {
          fullName: parsedAddress.fullName || '',
          organization: parsedAddress.organization || '',
          addressLine1: parsedAddress.addressLine1 || '',
          addressLine2: parsedAddress.addressLine2 || '',
          city,
          stateValue,
          zipCode,
          locality: city,
          administrativeArea: stateValue,
          postalCode: zipCode,
          countryCode
        }
      } catch (error) {
        this.debug('Could not parse venue address payload', { error })
        this.clearShippingAddress()
      }
		}
  }
}
